<template>
  <div class="app-root">

    <!-- ═══ 顶部栏 ═══ -->
    <header class="topbar">
      <div class="tb-left">
        <div class="tb-logo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-7z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
        </div>
        <span class="tb-brand">化工小助手</span>
      </div>

      <div class="tb-center">
        <!-- 访问计数器 -->
        <VisitorCounter :count="visitorCount" :animate="true" />
      </div>

      <div class="tb-right">
        <!-- B站 UP 主链接 -->
        <a href="https://www.bilibili.com/video/BV1oSDSBfERS/?share_source=copy_web" target="_blank" class="btn-bilibili" title="B站：化工小助手">
          <svg viewBox="0 0 24 24" fill="currentColor" style="width:18px;height:18px"><path d="M17.813 4.653h.854c1.51.054 2.769.578 3.773 1.574 1.004.995 1.524 2.249 1.56 3.76v7.36c-.036 1.51-.556 2.769-1.56 3.773s-2.262 1.524-3.773 1.56H5.333c-1.51-.036-2.769-.556-3.773-1.56S.036 18.858 0 17.347v-7.36c.036-1.511.556-2.765 1.56-3.76 1.004-.996 2.262-1.52 3.773-1.574h.774l-1.174-1.12a1.234 1.234 0 0 1-.373-.906c0-.356.124-.658.373-.907l.027-.027c.267-.249.573-.373.92-.373.347 0 .653.124.92.373L9.653 4.44c.071.071.134.142.187.213h4.267a.836.836 0 0 1 .16-.213l2.853-2.747c.267-.249.573-.373.92-.373.347 0 .662.151.929.4.267.249.391.551.391.907 0 .355-.124.657-.373.906zM5.333 7.24c-.746.018-1.373.276-1.88.773-.506.498-.769 1.13-.786 1.894v7.52c.017.764.28 1.395.786 1.893.507.498 1.134.756 1.88.773h13.334c.746-.017 1.373-.275 1.88-.773.506-.498.769-1.129.786-1.893v-7.52c-.017-.765-.28-1.396-.786-1.894-.507-.497-1.134-.755-1.88-.773zM8 11.107c.373 0 .684.124.933.373.25.249.383.569.4.96v1.173c-.017.391-.15.711-.4.96-.249.25-.56.374-.933.374s-.684-.125-.933-.374c-.25-.249-.383-.569-.4-.96V12.44c0-.373.129-.689.386-.947.258-.257.574-.386.947-.386zm8 0c.373 0 .684.124.933.373.25.249.383.569.4.96v1.173c-.017.391-.15.711-.4.96-.249.25-.56.374-.933.374s-.684-.125-.933-.374c-.25-.249-.383-.569-.4-.96V12.44c.017-.391.15-.711.4-.96.249-.249.56-.373.933-.373z"/></svg>
          <span>B站：化工小助手</span>
        </a>

        <!-- 联系我 + 悬浮二维码 -->
        <div class="contact-wrap"
             @mouseenter="showQr = true"
             @mouseleave="showQr = false"
             @click="showQr = !showQr">
          <button class="btn-contact">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            联系我
          </button>
          <Transition name="qr-fade">
            <div v-if="showQr" class="qr-popup">
              <div class="qr-arrow"></div>
              <img :src="'/qq.JPG'" alt="联系方式" class="qr-img" />
              <p class="qr-hint">扫码添加我QQ</p>
            </div>
          </Transition>
        </div>

        <button class="btn-dl-topbar" @click="handleDownload">
          <svg viewBox="0 0 20 20" fill="currentColor" style="width:16px;height:16px"><path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
          下载客户端
        </button>
      </div>
    </header>

    <div class="app-body">

      <!-- ═══ 左侧导航 ═══ -->
      <nav class="sidebar">
        <div class="sb-group-label">功能模块</div>
        <button
          v-for="(item, i) in navItems"
          :key="item.key"
          class="sb-item"
          :class="{ active: activeNav === item.key, locked: item.locked }"
          @click="!item.locked && switchNav(item.key)"
        >
          <div class="sb-icon" v-html="item.icon"></div>
          <span>{{ item.label }}</span>
          <span class="sb-badge" v-if="item.locked">待开发</span>
        </button>

        <div class="sb-spacer"></div>

        <div class="sb-bottom">
          <div class="sb-ver">v1.0.0</div>
        </div>
      </nav>

      <!-- ═══ 右侧内容区 ═══ -->
      <main class="content">
        <!-- 衡算模块 -->
        <BalanceModule
          v-if="activeNav === 'balance'"
          @download="handleDownload"
          @learnMore="showLearnMore"
        />

        <!-- 优化模块 -->
        <OptimizeModule
          v-else-if="activeNav === 'optimize'"
          @notifyMe="showNotifyDialog"
        />

        <!-- 设备选型模块 -->
        <EquipmentModule
          v-else-if="activeNav === 'equipment'"
          @notify="showNotifyDialog"
        />

        <!-- 管道选型模块 -->
        <PipelineModule
          v-else-if="activeNav === 'pipeline'"
          @notify="showNotifyDialog"
        />
      </main>
    </div>

    <!-- Footer -->
    <footer class="site-footer">
      &copy; 2025 化工小助手 &middot; 基于 Aspen Plus 引擎
    </footer>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import VisitorCounter from '../components/VisitorCounter/VisitorCounter.vue'
import BalanceModule from './modules/balance/BalanceModule.vue'
import OptimizeModule from './modules/optimize/OptimizeModule.vue'
import EquipmentModule from './modules/equipment/EquipmentModule.vue'
import PipelineModule from './modules/pipeline/PipelineModule.vue'
import { useVisitorCount } from '../components/VisitorCounter/useVisitorCount.js'

/* ─── 访问计数 ─── */
const { count: visitorCount } = useVisitorCount()

/* ─── 导航项 ─── */
const navItems = [
  {
    key: 'balance',
    label: 'Aspen 衡算',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>',
    locked: false
  },
  {
    key: 'optimize',
    label: '多目标优化',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06A1.65 1.65 0 0015 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.6 15a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.6a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9"/><circle cx="12" cy="12" r="4"/></svg>',
    locked: true
  },
  {
    key: 'equipment',
    label: '设备选型',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M4 12h16M12 4v16"/></svg>',
    locked: true
  },
  {
    key: 'pipeline',
    label: '管道选型',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 12h16M4 8h16M4 16h16"/></svg>',
    locked: true
  }
]

const activeNav = ref('balance')
const showQr = ref(false)

function switchNav(key) { activeNav.value = key }

/* ─── 下载 ─── */
const { recordDownload } = useVisitorCount()

function handleDownload(e) {
  if (e) e.preventDefault()
  // 记录下载次数
  recordDownload()
  // 执行下载
  const a = document.createElement('a')
  a.href = '/appaspen.exe'
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/* ─── 其他交互 ─── */
function showLearnMore() {
  alert('更多详细文档即将上线，敬请期待！')
}

function showNotifyDialog() {
  alert('已订阅上线通知，我们会在功能上线时第一时间通知您！')
}
</script>

<style scoped>
/* ─── Tokens ─── */
.app-root {
  --blue: #4285f4;
  --blue-dark: #1a73e8;
  --green: #34a853;
  --yellow: #fbbc04;
  --red: #ea4335;
  --bg: #ffffff;
  --bg-warm: #f8f9fa;
  --sidebar-bg: #f1f3f4;
  --surface: #ffffff;
  --text: #202124;
  --text-2: #5f6368;
  --text-3: #80868b;
  --border: #dadce0;
  --border-light: #e8eaed;

  font-family: 'Google Sans','PingFang SC','Microsoft YaHei','Noto Sans SC',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
  color: var(--text);
  background: var(--bg);
  height: 100vh;
  display: flex; flex-direction: column;
  overflow: hidden;
}

/* ═══ TOPBAR ═══ */
.topbar {
  height: 64px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 24px;
  border-bottom: 1px solid var(--border-light);
  background: var(--surface); z-index: 100;
}
.tb-left { display: flex; align-items: center; gap: 10px; }
.tb-logo { width: 28px; height: 28px; color: var(--blue); }
.tb-logo svg { width: 100%; height: 100%; }
.tb-brand { font-size: 17px; font-weight: 700; letter-spacing: -.01em; }

/* Topbar Center - 计数器 */
.tb-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

/* ═══ Topbar Right ═══ */
.tb-right { display: flex; align-items: center; gap: 10px; }

/* B站按钮 */
.btn-bilibili {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 20px; border: 1px solid var(--border);
  background: #fff; color: #00a1d6; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all .2s; font-family: inherit;
  text-decoration: none;
}
.btn-bilibili:hover {
  background: #00a1d6; color: #fff; border-color: #00a1d6;
  transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,161,214,.35);
}

.contact-wrap { position: relative; }
.btn-contact {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 18px; border-radius: 20px; border: 1px solid var(--border);
  background: transparent; color: var(--text); font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all .2s; font-family: inherit;
}
.btn-contact:hover { background: var(--bg-warm); border-color: var(--text-3); }
.btn-contact svg { width: 17px; height: 17px; }

.qr-popup {
  position: absolute; top: calc(100% + 12px); right: 0;
  z-index: 200; background: #fff;
  padding: 16px 16px 12px; border-radius: 14px;
  box-shadow: 0 8px 32px rgba(60,64,67,.22), 0 1px 4px rgba(60,64,67,.15);
  border: 1px solid var(--border-light); text-align: center;
  min-width: 180px;
}
.qr-arrow {
  position: absolute; top: -6px; right: 28px;
  width: 12px; height: 12px; background: #fff;
  border-left: 1px solid var(--border-light); border-top: 1px solid var(--border-light);
  transform: rotate(45deg);
}
.qr-img {
  width: 330px; height: auto; border-radius: 8px; display: block;
}
.qr-hint { margin: 8px 0 0; font-size: 12px; color: var(--text-3); }

/* QR fade transition */
.qr-fade-enter-active { transition: opacity .2s ease, transform .2s ease; }
.qr-fade-leave-active { transition: opacity .15s ease, transform .15s ease; }
.qr-fade-enter-from { opacity: 0; transform: translateY(-6px) scale(.96); }
.qr-fade-leave-to { opacity: 0; transform: translateY(-4px) scale(.98); }

.btn-dl-topbar {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 20px; border-radius: 20px; border: 1px solid var(--border);
  background: var(--blue); color: #fff; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all .2s; font-family: inherit;
}
.btn-dl-topbar:hover { background: var(--blue-dark); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(66,133,244,.35); }

/* ═══ APP BODY ═══ */
.app-body { flex: 1; display: flex; overflow: hidden; }

/* ═══ SIDEBAR ═══ */
.sidebar {
  width: 216px; flex-shrink: 0;
  background: var(--sidebar-bg);
  border-right: 1px solid var(--border-light);
  display: flex; flex-direction: column;
  padding: 16px 0;
}
.sb-group-label {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .08em; color: var(--text-3);
  padding: 0 20px 8px;
}

.sb-item {
  display: flex; align-items: center; gap: 10px;
  margin: 2px 8px; padding: 10px 14px; border-radius: 10px;
  border: none; background: transparent;
  cursor: pointer; font-size: 14px; font-family: inherit;
  color: var(--text-2); transition: all .15s;
  position: relative;
}
.sb-item svg, .sb-item :deep(svg) { width: 20px; height: 20px; flex-shrink: 0; opacity: .7; }
.sb-item:hover:not(.locked):not(.active) { background: rgba(0,0,0,.04); }
.sb-item.active {
  background: #e8f0fe; color: var(--blue-dark); font-weight: 600;
}
.sb-item.active svg, .sb-item.active :deep(svg) { opacity: 1; color: var(--blue); }

.sb-item.locked {
  opacity: .55; cursor: not-allowed;
}
.sb-badge {
  margin-left: auto; font-size: 10px; font-weight: 700;
  padding: 2px 8px; border-radius: 6px;
  background: #fef7e0; color: #b06000;
}

.sb-spacer { flex: 1; }
.sb-bottom { padding: 16px 20px; }
.sb-ver { font-size: 11px; color: var(--text-3); }

/* ═══ CONTENT ═══ */
.content {
  flex: 1;
  overflow-y: auto;
  background: var(--bg);
}

/* ═══ FOOTER ═══ */
.site-footer {
  flex-shrink: 0; text-align: center; padding: 12px;
  font-size: 12px; color: var(--text-3);
  border-top: 1px solid var(--border-light);
  background: var(--surface);
}

/* ─── Responsive ─── */
@media (max-width: 1024px) {
  .tb-center {
    position: static;
    transform: none;
    margin: 0 16px;
  }
}

@media (max-width: 768px) {
  .tb-center {
    display: none;
  }

  .sidebar { width: 64px; padding: 16px 0; }
  .sb-group-label, .sb-item span, .sb-badge, .sb-ver { display: none; }
  .sb-item { justify-content: center; padding: 12px 0; margin: 2px 8px; }

  .btn-bilibili span { display: none; }
  .btn-bilibili { padding: 8px; }

  .btn-dl-topbar span { display: none; }
  .btn-dl-topbar { padding: 8px 12px; }
}
</style>
